import ClassificationFilter from './filters/ClassificationFilter';
import HasExtractionFilter from './filters/HasExtractionFilter';
import SearchInputFilter from './filters/SearchInputFilter';
import TagsFilter from './filters/TagsFilter';
import ActiveFilterItemIndicator from './filters/ActiveFilterItemIndicator';
import ActiveFiltersContainer from './filters/ActiveFiltersContainer';
import { useFilterState } from './filters/useFilterState';
import { usePlatformData } from '../hooks/usePlatformData';

const STORAGE_KEY = 'reconstructed-documents-filters';

export interface ReconstructedDocumentsFilters {
  search: string;
  classification: string | null;
  tags: string[];
  hasExtraction: '' | 'yes' | 'no';
}

const defaultFilters: ReconstructedDocumentsFilters = {
  search: '',
  classification: null,
  tags: [],
  hasExtraction: '',
};

interface ReconstructedDocumentsFiltersProps {
  onFilterChange: (filters: ReconstructedDocumentsFilters) => void;
}

export default function ReconstructedDocumentsFiltersComponent({
  onFilterChange,
}: ReconstructedDocumentsFiltersProps) {
  const platformData = usePlatformData();
  const { filters, setFilters, clear } = useFilterState<ReconstructedDocumentsFilters>({
    storageKey: STORAGE_KEY,
    defaults: defaultFilters,
    onFilterChange,
  });

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value });
  };

  const handleClassificationChange = (value: string) => {
    const classification = value === '' ? null : value;
    setFilters({ ...filters, classification });
  };

  const handleTagChange = (value: string) => {
    const selectedTag = value;
    if (!selectedTag) {
      setFilters({ ...filters, tags: [] });
      return;
    }
    if (!filters.tags.includes(selectedTag)) {
      setFilters({ ...filters, tags: [...filters.tags, selectedTag] });
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFilters({
      ...filters,
      tags: filters.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleHasExtractionChange = (value: '' | 'yes' | 'no') => {
    setFilters({ ...filters, hasExtraction: value });
  };

  const hasActiveFilters =
    filters.search ||
    filters.classification ||
    filters.hasExtraction ||
    filters.tags.length > 0;

  return (
    <div className="rounded-lg mb-6">
      <div className="hidden sm:block">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <SearchInputFilter
            filters={{ search: filters.search }}
            handleSearchChange={handleSearchChange}
            placeholder="Search (id-prefix / filename / ESN)"
          />
          <ClassificationFilter
            filters={{ classification: filters.classification }}
            handleClassificationChange={handleClassificationChange}
            categoriesWithGroups={[
              {
                label: 'Document Categories',
                options:
                  platformData?.categories?.data?.documentCategories
                    .map((item) => ({
                      label: item,
                      value: item,
                    }))
                    ?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
              },
              {
                label: 'Page Categories',
                options:
                  platformData?.categories?.data?.pageCategories
                    .map((item) => ({
                      label: item,
                      value: item,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
              },
            ]}
          />
          <TagsFilter
            filters={{ tags: filters.tags }}
            handleTagChange={handleTagChange}
          />
          <HasExtractionFilter
            filters={{ hasExtraction: filters.hasExtraction }}
            handleChange={handleHasExtractionChange}
          />
        </div>

        <ActiveFiltersContainer
          handleClearFilters={clear}
          hasActiveFilters={!!hasActiveFilters}
        >
          {filters.search && (
            <ActiveFilterItemIndicator
              label="Search"
              value={filters.search}
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              onRemove={() => handleSearchChange('')}
            />
          )}

          {filters.classification && (
            <ActiveFilterItemIndicator
              label="Classification"
              value={filters.classification}
              className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              onRemove={() => handleClassificationChange('')}
            />
          )}

          {filters.tags.map((tag) => (
            <ActiveFilterItemIndicator
              key={tag}
              label="Tag"
              value={tag}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => handleRemoveTag(tag)}
            />
          ))}

          {filters.hasExtraction && (
            <ActiveFilterItemIndicator
              label="Extraction"
              value={filters.hasExtraction === 'yes' ? 'With' : 'Without'}
              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              onRemove={() => handleHasExtractionChange('')}
            />
          )}
        </ActiveFiltersContainer>
      </div>
    </div>
  );
}
